import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { Panel } from "../../../resusableComponents/funnelFuel";
import { hasAccess } from "../../../resusableComponents/hoc/AccessManagement";

import { auditFunnelFuelExport } from "../../../redux/services/campaign";

import { buildCsv, downloadCsv, EXPORT_COLUMNS, exportFilename, filtersPayload, newExportId } from "../lib/csv";
import { useFilters } from "../lib/FilterContext";
import { FN_COLOR_OTHER } from "../lib/constants";
import { fmt } from "../lib/selectors";

/*
  Export: the CSV is built here, in the browser, from the filtered leads the table is showing, so the file always matches
  the screen. It carries every column of the table plus Industry and Country, which the table leaves out. The server is
  told about each export (audit log) but the export never waits for, or depends on, that call: if it fails the file is
  still downloaded and the failure is only logged to the console for support. The button is gated by the
  `funnelfuel-dashboard-export-leads` operation (campaigns > dashboard).
*/

// The register is windowed (only the rows in view are in the DOM). At 10,000 rows an unwindowed table measured
// 172 ms in the reference, and it repeats on every filter click. Row height is fixed, so no measuring pass is needed.
// The permission operation (campaigns > dashboard) that shows the export button; BE/DB must seed it for the role.
const EXPORT_OPERATION = "funnelfuel-dashboard-export-leads";

const ROW_HEIGHT = 43;
const VIEWPORT = 640;
const OVERSCAN = 6;

const LeadRow = ({ lead, lookups, colors, formatDate, index }) => (
  <div className="ff-reg-row" role="row" aria-rowindex={index + 2} style={{ height: ROW_HEIGHT, top: index * ROW_HEIGHT }}>
    <span role="cell" className="ff-muted ff-nowrap">{lead.activityDate ? formatDate(lead.activityDate) : "—"}</span>
    <span role="cell" className="ff-truncate-fill" title={lead.companyName || undefined}>{lead.companyName || "—"}</span>
    <span role="cell" className="ff-truncate-fill ff-2" title={lead.jobTitle || undefined}>{lead.jobTitle || "—"}</span>
    <span role="cell" className="ff-truncate-fill ff-muted">{lead.seniority || "—"}</span>
    <span role="cell" className="ff-truncate-fill">
      {/* Colour dot AND the text label: job function is never conveyed by colour alone. */}
      {lead.jobFunction
        ? <span className="ff-fn"><span className="ff-swatch" style={{ background: colors[lead.jobFunction] || FN_COLOR_OTHER }} aria-hidden="true" />{lead.jobFunction}</span>
        : "—"}
    </span>
    <span role="cell" className="ff-truncate-fill ff-muted" title={lookups.assetName[lead.assetKey]}>{lookups.assetName[lead.assetKey] || "—"}</span>
    <span role="cell" className="ff-truncate-fill ff-muted" title={lookups.lineItemName[lead.lineItemId]}>{lookups.lineItemName[lead.lineItemId] || "—"}</span>
  </div>
);

const VirtualRows = ({ rows, lookups, colors, formatDate, label }) => {
  const [scrollTop, setScrollTop] = useState(0);

  const height = Math.min(VIEWPORT, rows.length * ROW_HEIGHT);
  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const count = Math.ceil(height / ROW_HEIGHT) + OVERSCAN * 2;

  return (
    // Scrollable regions must be keyboard focusable so the rows can be reached without a mouse.
    <div role="rowgroup" className="ff-reg-body" tabIndex={0} aria-label={label} style={{ height }} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
      <div style={{ height: rows.length * ROW_HEIGHT, position: "relative" }}>
        {/* Keyed on the stable lead id, never the array index: with an index, filtering recycles rows into the wrong content. */}
        {rows.slice(first, first + count).map((lead, i) => (
          <LeadRow key={lead.id} lead={lead} lookups={lookups} colors={colors} formatDate={formatDate} index={first + i} />
        ))}
      </div>
    </div>
  );
};

/* The register itself: rows in the server's order (orderedLeadIds), reflecting every active filter. */
export const LeadRegisterPanel = ({ model, view, active }) => {
  const intl = useIntl();
  const { filters } = useFilters();
  const rows = view.register;
  const [exportState, setExportState] = useState({ status: "idle", count: 0 });

  // A result message belongs to the selection it was made for.
  useEffect(() => { setExportState({ status: "idle", count: 0 }); }, [rows]);

  const t = (id, defaultMessage, values) => intl.formatMessage({ id: `ff_dashboard.register.${id}`, defaultMessage }, values);
  // UTC, so a date-only value never shifts a day in a browser west of Greenwich.
  const formatDate = (date) => intl.formatDate(new Date(date), { day: "2-digit", month: "short", timeZone: "UTC" });

  const onExport = () => {
    if (!rows.length) { return; }

    const fileName = exportFilename(model.campaign.name, active);
    const exportId = newExportId();

    // Sent first, never waited for: it must not delay the file, and a failure must not stop it.
    Promise.resolve(auditFunnelFuelExport(model.campaign.id, { exportId, rowCount: rows.length, filters: filtersPayload(filters) }))
      .then((audit) => {
        if (!audit || audit.flag !== true) { console.warn(`FunnelFuel export ${exportId} was not audit-logged: ${audit && audit.message}`); }
      })
      .catch((error) => console.warn(`FunnelFuel export ${exportId} was not audit-logged: ${error && error.message}`));

    const labels = {};
    EXPORT_COLUMNS.forEach((column) => { labels[column.key] = intl.formatMessage({ id: column.labelId, defaultMessage: column.defaultLabel }); });

    downloadCsv(fileName, buildCsv(rows, labels, model.lookups));
    setExportState({ status: "done", count: rows.length });
  };

  const exportLabel = (id, defaultMessage, values) => intl.formatMessage({ id: `ff_dashboard.export.${id}`, defaultMessage }, values);
  const canExport = hasAccess("campaigns", "dashboard", [EXPORT_OPERATION]);

  const columns = [
    ["col_date", "Date"], ["col_company", "Company"], ["col_job_title", "Job title"], ["col_seniority", "Seniority"],
    ["col_job_function", "Job function"], ["col_asset", "Asset"], ["col_line_item", "Line item"]
  ];

  return (
    <Panel
      title={t("title", "Lead register")}
      caption={t("caption", "Reflects every active filter · {count} rows", { count: fmt(rows.length) })}
      isEmpty={rows.length === 0}
      emptyMessage={intl.formatMessage(active
        ? { id: "ff_dashboard.empty.leads_filtered", defaultMessage: "No leads match the current filters." }
        : { id: "ff_dashboard.empty.leads_none", defaultMessage: "No leads have been delivered yet." })}
      flush
      actions={canExport && (
        <div className="ff-export">
          {exportState.status === "done" && <span className="ff-export-msg" role="status">{exportLabel("done", "Exported {count} leads", { count: exportState.count })}</span>}
          <button type="button" className="ff-btn-export" disabled={!rows.length} onClick={onExport}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
            </svg>
            {!rows.length
              ? exportLabel("nothing", "Nothing to export")
              : exportLabel(active ? "button_selected" : "button", "Export {count} leads", { count: rows.length })}
          </button>
        </div>
      )}
    >
      <div className="ff-register" role="table" aria-label={t("aria", "Lead register")} aria-rowcount={rows.length + 1}>
        <div className="ff-reg-head" role="row" aria-rowindex={1}>
          {columns.map(([id, fallback], i) => (
            <span key={id} role="columnheader" className={i === 2 ? "ff-2" : undefined}>{t(id, fallback)}</span>
          ))}
        </div>
        <VirtualRows rows={rows} lookups={model.lookups} colors={model.fnColors} formatDate={formatDate} label={t("aria", "Lead register")} />
      </div>
    </Panel>
  );
};
